type TargetKey<Schema> = Extract<keyof Schema, string>

type ParsedValue<T> = [T] extends ['true' | 'false'] ? boolean : T

type InferElement<Key extends string, ElementConfig> = [ElementConfig] extends [
  HTMLElement,
]
  ? ElementConfig
  : Key extends keyof ElementConfig
    ? ElementConfig[Key] extends HTMLElement
      ? ElementConfig[Key]
      : HTMLElement
    : HTMLElement

interface DomSchemaUtils<Schema, ElementConfig> {
  /**
   * Sets an attribute value using native setAttribute. Accepts native booleans
   * for boolean strings.
   */
  set: <K extends TargetKey<Schema>>(
    element: HTMLElement,
    key: K,
    value: (Schema[K] | ParsedValue<Schema[K]>) & (string | number | boolean),
  ) => void

  /**
   * Retrieves an attribute value, automatically parsing boolean strings into
   * native booleans.
   */
  get: <K extends TargetKey<Schema>>(
    element: HTMLElement,
    key: K,
  ) => ParsedValue<Schema[K]> | null

  /**
   * Queries for a single element possessing this key. Automatically types the
   * return subclass.
   */
  query: <K extends TargetKey<Schema>>(
    key: K,
    parent?: Document | HTMLElement,
  ) => InferElement<K, ElementConfig> | null

  /**
   * Queries for all elements possessing this key. Automatically types the
   * return subclass list.
   */
  queryAll: <K extends TargetKey<Schema>>(
    key: K,
    parent?: Document | HTMLElement,
  ) => NodeListOf<InferElement<K, ElementConfig>>

  /** Queries for a single element matching a specific key-value pair. */
  queryValue: <K extends TargetKey<Schema>>(
    key: K,
    value: (Schema[K] | ParsedValue<Schema[K]>) & (string | number | boolean),
    parent?: Document | HTMLElement,
  ) => InferElement<K, ElementConfig> | null

  /** Queries for all elements matching a specific key-value pair. */
  queryValueAll: <K extends TargetKey<Schema>>(
    key: K,
    value: (Schema[K] | ParsedValue<Schema[K]>) & (string | number | boolean),
    parent?: Document | HTMLElement,
  ) => NodeListOf<InferElement<K, ElementConfig>>

  /**
   * Type guard verifying an element has this attribute key, narrowing it to the
   * mapped subclass.
   */
  has: <K extends TargetKey<Schema>>(
    element: HTMLElement,
    key: K,
  ) => element is InferElement<K, ElementConfig>

  /**
   * Type guard verifying an element has this exact attribute value, narrowing
   * it to the mapped subclass.
   */
  hasValue: <K extends TargetKey<Schema>>(
    element: HTMLElement,
    key: K,
    value: (Schema[K] | ParsedValue<Schema[K]>) & (string | number | boolean),
  ) => element is InferElement<K, ElementConfig>
}

/**
 * Creates a pre-bound, fully type-safe set of attribute utilities for a
 * specific element schema. Operates purely on native Web APIs and made for use
 * in vanilla JS and React.
 */
export function defineDomSchema<
  Schema,
  ElementConfig = HTMLElement,
>(): DomSchemaUtils<Schema, ElementConfig> {
  return {
    set(element, key, value) {
      element.setAttribute(key, String(value))
    },

    get<K extends TargetKey<Schema>>(element: HTMLElement, key: K) {
      const rawValue = element.getAttribute(key)
      if (rawValue === null) return null
      if (rawValue === 'true') return true as unknown as ParsedValue<Schema[K]>
      if (rawValue === 'false')
        return false as unknown as ParsedValue<Schema[K]>
      return rawValue as unknown as ParsedValue<Schema[K]>
    },

    query<K extends TargetKey<Schema>>(
      key: K,
      parent?: Document | HTMLElement,
    ) {
      return (parent ?? document).querySelector(`[${key}]`) as InferElement<
        K,
        ElementConfig
      > | null
    },

    queryAll<K extends TargetKey<Schema>>(
      key: K,
      parent?: Document | HTMLElement,
    ) {
      return (parent ?? document).querySelectorAll(`[${key}]`) as NodeListOf<
        InferElement<K, ElementConfig>
      >
    },

    queryValue<K extends TargetKey<Schema>>(
      key: K,
      value: (Schema[K] | ParsedValue<Schema[K]>) & (string | number | boolean),
      parent?: Document | HTMLElement,
    ) {
      return (parent ?? document).querySelector(
        `[${key}="${String(value)}"]`,
      ) as InferElement<K, ElementConfig> | null
    },

    queryValueAll<K extends TargetKey<Schema>>(
      key: K,
      value: (Schema[K] | ParsedValue<Schema[K]>) & (string | number | boolean),
      parent?: Document | HTMLElement,
    ) {
      return (parent ?? document).querySelectorAll(
        `[${key}="${String(value)}"]`,
      ) as NodeListOf<InferElement<K, ElementConfig>>
    },

    has<K extends TargetKey<Schema>>(
      element: HTMLElement,
      key: K,
    ): element is InferElement<K, ElementConfig> {
      return element.hasAttribute(key)
    },

    hasValue<K extends TargetKey<Schema>>(
      element: HTMLElement,
      key: K,
      value: (Schema[K] | ParsedValue<Schema[K]>) & (string | number | boolean),
    ): element is InferElement<K, ElementConfig> {
      return element.getAttribute(key) === String(value)
    },
  }
}
